import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sectorAlignmentApi } from '../api/sectorAlignmentApi';
import type { RealignmentJob, SectorDesk } from '../api/sectorAlignmentApi';

const KEY = 'sector-alignment';
const JOB_KEY = [KEY, 'job'];
const RUNNING = ['waiting', 'active', 'delayed'];

export const isJobRunning = (job: { state: string } | undefined): boolean => Boolean(job && RUNNING.includes(job.state));

export const jobRefetchInterval = (job: { state: string } | undefined): number | false => (isJobRunning(job) ? 1000 : false);

/** Analysis is a mutation on purpose: nothing is cached, every call recomputes (one desk or both). */
export const useSectorAnalysis = () =>
  useMutation({ mutationFn: (sd?: SectorDesk) => sectorAlignmentApi.analysis(sd), retry: false });

/** Settles on 202 and 409 alike: either way the job is worth re-reading, which restarts polling. */
export const useStartRealignment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sd: SectorDesk) => sectorAlignmentApi.realign(sd),
    retry: false,
    onSettled: () => qc.invalidateQueries({ queryKey: JOB_KEY }),
  });
};

/** Polls the job (when `enabled`); when it leaves the running states, re-runs that desk's analysis (its result carries lastRun). */
export const useSectorRealignmentJob = (rerunDesk: (sd: SectorDesk) => void, { enabled = true } = {}) => {
  const { data: job } = useQuery<RealignmentJob>({
    queryKey: JOB_KEY,
    enabled,
    queryFn: sectorAlignmentApi.job,
    refetchInterval: (q) => jobRefetchInterval(q.state.data),
  });
  const running = useRef<SectorDesk | null>(null);

  useEffect(() => {
    if (!job) return;
    if (isJobRunning(job)) {
      running.current = job.sd ?? null;
    } else if (running.current !== null) {
      const sd = running.current;
      running.current = null;
      rerunDesk(sd);
    }
  }, [job, rerunDesk]);

  return { job };
};
